import {Room} from '@/components/estimates/common/controls/values';
import {isPositiveNumber, parseDecimalNumber} from '@/utils';
import {round} from 'lodash-es';

type RawDimensions = [string, string, string];
type Dimensions = [number, number] | number;

export class RoomControlState {

    public get area(): number {
        return this.dimensions.map(this.convertDimensions)
            .filter((dim): dim is Dimensions => dim !== null)
            .map(dim => round(Array.isArray(dim) ? dim[0] * dim[1] : dim, 2))
            .reduce((a, b) => a + b, 0);
    }

    public get value(): Room | null {
        const dimensions = this.dimensions.map(this.convertDimensions);

        if (dimensions.includes(null) || this.roomType === '') {
            return null;
        }

        return new Room(
            this.roomType,
            this.isAddition,
            dimensions as Dimensions[],
        );
    }

    public static fromValue(value: Room | null): RoomControlState {
        const state = new RoomControlState();

        if (value === null) {
            return state;
        }

        state.roomType = value.type;
        state.isAddition = value.isAddition;

        state.dimensions = value.dimensions.map(
            (dimension): RawDimensions => Array.isArray(dimension)
                ? ['' + dimension[0], '' + dimension[1], '' + round(dimension[0] * dimension[1], 2)]
                : ['', '', '' + dimension],
        );

        return state;
    }

    public roomType: string = '';
    public dimensions: RawDimensions[] = [['', '', '']];
    public isAddition: boolean = false;

    public updateRoomType(roomType: string, isAddition: boolean): void {
        this.roomType = roomType;
        this.isAddition = isAddition;
    }

    public addDimensions(dimensions: RawDimensions): void {
        this.dimensions.push(dimensions);
    }

    public updateDimensions(index: number, dimensions: RawDimensions): void {
        this.dimensions.splice(index, 1, dimensions);
    }

    public removeDimensions(index: number): void {
        this.dimensions = this.dimensions.slice(0, index).concat(this.dimensions.slice(index + 1));
    }

    public areDimensionsValid(index: number): boolean {
        return this.convertDimensions(this.dimensions[index]) !== null;
    }

    private convertDimensions(rawDimensions: RawDimensions): Dimensions | null {
        const dimensions = rawDimensions.map(dim => parseDecimalNumber(dim));

        if (dimensions.some(dim => !isPositiveNumber(dim))) {
            const area = dimensions[2];

            return isPositiveNumber(area) ? area : null;
        }

        return [dimensions[0], dimensions[1]] as [number, number];
    }
}
